import { z } from "zod";
import { PlayerResultRecordSchema, PlayerSelfEnvelopeSchema, PlayerSelfRecordSchema, PublicLobbyRecordSchema } from "@/stores/schemas";
import type { PlayerSelfEnvelope, PublicLobbyRecord } from "@/stores/types";
import { publicLifeWithheld } from "@/stores/lifeState";
import type { RoomBackend, Unsubscribe } from "./backend";

export type SnapshotIssue = { path: (string | number)[]; code: string };
export type Snapshot<T> =
  | { status: "ready"; data: T }
  | { status: "waiting" }
  | { status: "invalid"; issues: SnapshotIssue[] };
export type PublicSnapshot = Snapshot<PublicLobbyRecord> | { status: "ended" };
export const WAITING = { status: "waiting" } as const;
export const DATA_ERROR_MESSAGE = "We couldn't read the latest game data. Waiting for an update from the Storyteller.";
export const CONNECTION_ERROR_MESSAGE = "We couldn't receive game data. Check your connection and try again.";
const isDevRuntime = (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV === true;

// Omission/null means an absent RTDB node, not a type-coercion opportunity.
const emptyNode = <T extends z.ZodTypeAny>(schema: T, fallback: unknown) =>
  z.preprocess((value) => value == null ? fallback : value, schema);
const optionalNode = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((value) => value === null ? undefined : value, schema.optional());
const id = z.string().min(1).refine((value) => value.trim().length > 0);
const list = z.array(id);
const publicPlayer = PublicLobbyRecordSchema.shape.players.valueSchema.extend({
  id, name: id,
  publicDisplayRole: optionalNode(id),
});
const publicShape = PublicLobbyRecordSchema.extend({
  code: id,
  scriptId: id,
  players: emptyNode(z.record(id, publicPlayer), {}),
  seatOrder: emptyNode(list, []),
  fabled: emptyNode(list, []),
  lorics: emptyNode(list, []),
  winner: optionalNode(PublicLobbyRecordSchema.shape.winner.unwrap()),
  status: optionalNode(z.literal("ended")),
});
// Phase 10H: the self record arrives inside its envelope (reveal token, own
// Life). Both envelope fields are optional: a pre-10H Storyteller writes
// neither, and the identity is still complete without them.
const selfShape = PlayerSelfEnvelopeSchema.extend({
  shownRole: id,
  bluffs: optionalNode(list),
  minions: optionalNode(PlayerSelfRecordSchema.shape.minions.unwrap()),
  extraText: optionalNode(z.string()),
  demon: optionalNode(PlayerSelfRecordSchema.shape.demon.unwrap()),
  revealToken: optionalNode(PlayerSelfEnvelopeSchema.shape.revealToken.unwrap()),
  life: optionalNode(PlayerSelfEnvelopeSchema.shape.life.unwrap()),
});
const request = z.string().min(1).max(20).refine((value) =>
  value.trim().length > 0 && !value.startsWith(" ") && !value.endsWith(" ") && !/[\r\n\t]/.test(value));
const presenceEntry = z.object({
  online: z.boolean(), lastSeen: z.number().int().nonnegative(),
});
export type PresenceMap = Record<string, z.infer<typeof presenceEntry>>;

function invalid(error: z.ZodError): { status: "invalid"; issues: SnapshotIssue[] } {
  // Keep field locations/codes for developers, never raw values or Zod messages
  // (enum errors may embed private input in their text).
  return { status: "invalid", issues: error.issues.map(({ path, code }) => ({ path, code })) };
}
function invalidField(field: string, code: string): { status: "invalid"; issues: SnapshotIssue[] } {
  return { status: "invalid", issues: [{ path: [field], code }] };
}
function parse<T>(schema: z.ZodType<T, z.ZodTypeDef, unknown>, raw: unknown): Snapshot<T> {
  const result = schema.safeParse(raw);
  return result.success ? { status: "ready", data: result.data } : invalid(result.error);
}

export function decodePublicSnapshot(raw: unknown, expectedCode?: string): PublicSnapshot {
  if (raw == null) return WAITING;
  // Validate present fields even in a partial record. Missing root scalars are
  // incomplete data; wrong types/values and broken player records are invalid.
  const partial = publicShape.partial().safeParse(raw);
  if (!partial.success) return invalid(partial.error);
  if (partial.data.code !== undefined && expectedCode !== undefined && partial.data.code !== expectedCode) {
    return invalidField("code", "lobby_mismatch");
  }
  const complete = publicShape.safeParse(raw);
  if (!complete.success) {
    // endLobby may publish only this validated signal before other fields exist.
    if (partial.data.status === "ended") return { status: "ended" };
    return WAITING;
  }
  const data = complete.data;
  for (const [key, player] of Object.entries(data.players)) {
    if (key !== player.id) return invalidField("players", "id_mismatch");
  }
  // Phase 10F (v24, Section 10): Life State is public only outside Night.
  // During Night every Life field is DROPPED here as well -- fail closed even
  // if an older writer still sent it -- so no view can render a mid-Night
  // Life change. Outside Night a record without `alive`/`ghostVote` is an
  // incomplete projection, never "withheld".
  if (publicLifeWithheld(data.phase)) {
    for (const player of Object.values(data.players)) {
      delete player.alive;
      delete player.ghostVote;
      delete player.exiled;
    }
  } else if (Object.values(data.players).some((player) => typeof player.alive !== "boolean" || typeof player.ghostVote !== "boolean")) {
    return invalidField("players", "life_missing");
  }
  if (new Set(data.seatOrder).size !== data.seatOrder.length) return invalidField("seatOrder", "duplicate_id");
  // References can arrive before their records; don't expose a partial model.
  if (data.seatOrder.some((key) => !Object.hasOwn(data.players, key))) return WAITING;
  return { status: "ready", data };
}

/**
 * The player's own identity. No shown character -> WAITING (no identity yet).
 * Phase 10E: a valid shown character WITHOUT an alignment is a complete
 * identity for an ordinary participant and a Traveler alike (the Storyteller
 * deliberately did not tell the alignment, or a Traveler's is unresolved) --
 * rendered as that character with no alignment label, never as WAITING. The
 * self node is written whole by the Storyteller (writeProjections), so a
 * missing alignment is never a half-arrived write.
 */
export function decodeSelfSnapshot(raw: unknown): Snapshot<PlayerSelfEnvelope> {
  if (raw == null) return WAITING;
  const partial = selfShape.partial().safeParse(raw);
  if (!partial.success) return invalid(partial.error);
  if (partial.data.shownRole === undefined) return WAITING;
  return parse(selfShape, raw);
}
/**
 * Phase 10H: this player's own results/{uid} -- the immutable player-safe
 * terminal result. Exact schema (strict: an unknown key is invalid, never
 * stripped into validity), and the record must name `expectedSessionId`
 * (10H-AC-056): a result from any other session is invalid, never shown.
 * Absent is "waiting" (the caller treats a confirmed absence after the session
 * ended as "no recorded result").
 */
export type PlayerResultRecord = z.infer<typeof PlayerResultRecordSchema>;
export function decodePlayerResult(raw: unknown, expectedSessionId: string): Snapshot<PlayerResultRecord> {
  if (raw == null) return WAITING;
  const parsed = parse(PlayerResultRecordSchema, raw);
  if (parsed.status !== "ready") return parsed;
  if (parsed.data.sessionId !== expectedSessionId) return invalidField("sessionId", "session_mismatch");
  return parsed;
}
export const decodeRosterEntry = (raw: unknown): Snapshot<string> => raw == null ? WAITING : parse(id, raw);
export const decodeJoinRequest = (raw: unknown): Snapshot<string> => raw == null ? WAITING : parse(request, raw);
export const decodeRoster = (raw: unknown): Snapshot<Record<string, string>> => parse(emptyNode(z.record(id, id), {}), raw);
/** Phase 9R.2 (Astra R1): the Storyteller-only record naming which
 * participation instance each roster binding seats (see
 * rosterParticipantPath). `name` is the seat-time name, bounded exactly like
 * a join request; `.strict()` because the record is exactly these fields. */
const rosterParticipant = z.object({ playerId: id, participantId: id, name: request }).strict();
export type RosterParticipantRecord = z.infer<typeof rosterParticipant>;
export const decodeRosterParticipants = (raw: unknown): Snapshot<Record<string, RosterParticipantRecord>> =>
  parse(emptyNode(z.record(id, rosterParticipant), {}), raw);
/** One binding's record (rosterParticipants/{uid}); absent is "waiting". */
export const decodeRosterParticipant = (raw: unknown): Snapshot<RosterParticipantRecord> =>
  raw == null ? WAITING : parse(rosterParticipant, raw);
/** Phase 9R.6: the Storyteller-only receipt of a committed membership
 * revocation (see membershipRevocationPath) -- which exact participation
 * instance was revoked from which seat, and the local occupancy completion
 * the Storyteller's command owed. `.strict()`: exactly these fields, no
 * Role/alignment/private data. */
const membershipRevocation = z.object({ playerId: id, participantId: id, action: z.enum(["unseat", "remove"]) }).strict();
export type MembershipRevocationRecord = z.infer<typeof membershipRevocation>;
export const decodeMembershipRevocations = (raw: unknown): Snapshot<Record<string, MembershipRevocationRecord>> =>
  parse(emptyNode(z.record(id, membershipRevocation), {}), raw);
export const decodeJoinRequests = (raw: unknown): Snapshot<Record<string, string>> => parse(emptyNode(z.record(id, request), {}), raw);
/** A player's own leaveRequests/{uid}. Strictly `true` or absent — never
 * trusted as authority, only as a hint for handshake reconciliation. */
export const decodeLeaveRequest = (raw: unknown): Snapshot<boolean> =>
  raw == null ? { status: "ready", data: false } : parse(z.literal(true), raw);
/**
 * One malicious/malformed presence row must never poison the whole map: a
 * single bad entry is dropped, not treated as atomic decode failure for
 * every other (valid) uid's presence. Only a structurally non-record root
 * (not even an object) is reported invalid.
 */
export function decodePresence(raw: unknown): Snapshot<PresenceMap> {
  if (raw == null) return { status: "ready", data: {} };
  if (typeof raw !== "object" || Array.isArray(raw)) return invalidField("presence", "invalid_type");
  const data: PresenceMap = {};
  for (const [uid, value] of Object.entries(raw as Record<string, unknown>)) {
    const entry = presenceEntry.safeParse(value);
    if (entry.success) data[uid] = entry.data;
  }
  return { status: "ready", data };
}
export const decodeLobbyStatus = (raw: unknown): Snapshot<"active" | "ended"> =>
  raw == null ? { status: "ready", data: "active" } : parse(z.enum(["active", "ended"]), raw);

export function reportSnapshotProblem(scope: string, issues: SnapshotIssue[]) {
  if (isDevRuntime) console.warn("[remote snapshot]", scope, issues);
}
export class SnapshotValidationError extends Error {
  constructor() { super(DATA_ERROR_MESSAGE); }
}

/** One subscription boundary: decode every event; keep listening after bad data. */
export function subscribeDecoded<T extends { status: string }>(
  backend: RoomBackend, path: string, decode: (raw: unknown) => T, receive: (value: T) => void,
  onReadError?: () => void,
): Unsubscribe {
  return backend.subscribe(path, (raw) => {
    const decoded = decode(raw);
    if ("issues" in decoded && Array.isArray(decoded.issues)) {
      // Generic transport diagnostics are supplied by concrete decoders below.
      if (isDevRuntime) console.warn("[remote snapshot]", path, decoded.issues);
    }
    receive(decoded);
  }, () => {
    reportSnapshotProblem(path, [{ path: [], code: "read_failed" }]);
    onReadError?.();
  });
}
